import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { api, FctReport } from '@/api';
import { GetRadiologistNoteResponse } from '@/api/model';
import { useAddendum } from '@/core/addendum';
import { useIsDDFeatureFlagEnabled } from '@/core/data-dog';
import { useStudyGuard } from '@/shared/guard';
import { useFctReport } from '@/shared/hook';
import { formatEditorString, isEmpty, isNotEmpty } from '@/shared/util';

import { useOrganLinksWithHighScore } from './use-organ-links-with-high-score.hook';
import { usePreviousStudies } from './use-previous-studies.hook';

export function useValidateFinalImpression() {
  const queryClient = useQueryClient();
  const { findingsLinks } = useOrganLinksWithHighScore();
  const { hasPreviousStudyResults, isError: isPriorStudiesError } = usePreviousStudies();
  const { report: currentReport } = useFctReport();
  const { isPoetryStudy } = useStudyGuard();

  const { isAddendumMode } = useAddendum();
  const isConsultNotesEnabled = useIsDDFeatureFlagEnabled('FCT_CARE_PLAN_NOTES');

  const shouldLoadConsultNote =
    isConsultNotesEnabled && isPoetryStudy && !!currentReport?.carePlanId && isNotEmpty(currentReport?.uuid);

  const {
    data: consultNoteQueryData,
  } = api.fct.radiologistNote.query.useGetRadiologistNote(
    {
      payload: {
        reportUuid: currentReport?.uuid ?? '',
      },
    },
    {
      enabled: shouldLoadConsultNote,
    },
  );

  const validateFinalImpression = useCallback(
    (report: FctReport, addendum?: string) => {
      const isFinalImpressionsRequired = report?.config?.isFinalImpressionsRequired !== false;

      const isUrgencyCategoryRequired = report?.config?.isUrgencyCategoryRequired !== false;

      const missingFinalImpressions =
        isFinalImpressionsRequired && isEmpty(formatEditorString(report?.finalImpressions ?? ''));

      const isReportAcknowledged = isEmpty(findingsLinks) || report?.acknowledgement;

      const missingUrgencyCategory = isUrgencyCategoryRequired && isEmpty(report?.urgencyCategory);

      // priorStudyIds validation:
      // - null: user selected "No relevant comparison available" (valid)
      // - undefined: no selection made (invalid)
      // - []: empty array means no scans selected (invalid)
      // - ['id']: scans selected (valid)
      // When the prior studies API fails, treat as missing to prevent fail-open
      const shouldRequireComparison = hasPreviousStudyResults || isPriorStudiesError;

      const missingComparisonData =
        shouldRequireComparison &&
        (report?.priorStudyIds === undefined ||
          (Array.isArray(report?.priorStudyIds) && isEmpty(report.priorStudyIds)));

      const missingAddendumText = isAddendumMode && isEmpty(formatEditorString(addendum ?? ''));
      const shouldRequireConsultNote =
        isConsultNotesEnabled && isPoetryStudy && !!report?.carePlanId && report?.urgencyCategory === 'IV';

      const consultNote =
        queryClient.getQueryData<GetRadiologistNoteResponse>([
          api.fct.radiologistNote.KEY.GET_RADIOLOGIST_NOTE,
          report?.uuid ?? '',
        ]) ?? consultNoteQueryData;

      const missingConsultNote =
        shouldRequireConsultNote && isEmpty(consultNote?.draft?.content ?? '');

      return (
        missingFinalImpressions ||
        missingComparisonData ||
        !isReportAcknowledged ||
        missingUrgencyCategory ||
        missingAddendumText ||
        missingConsultNote
      );
    },
    [
      consultNoteQueryData,
      findingsLinks,
      hasPreviousStudyResults,
      isPriorStudiesError,
      isAddendumMode,
      isConsultNotesEnabled,
      isPoetryStudy,
      queryClient,
    ],
  );

  return validateFinalImpression;
}
